/* eslint-disable react/prop-types */
import Moment from 'react-moment';
import { useRouter } from 'next/router';
import Blocks from '@/components/Blocks';
import Container from '@/components/Container';
import Layout from '@/components/Layout';
import BlockHeading from '@/components/BlockHeading';
import BlockFeatureImage from '@/components/BlockFeatureImage';
import SocialShare from '@/components/SocialShare';
import getPostTypeStaticPaths from '@/functions/wp/posts/getPostTypeStaticPaths';
import getPostTypeStaticProps from '@/functions/wp/posts/getPostTypeStaticProps';
import BlockArticleColumns from '@/components/BlockArticleColumns';
import AuthorSocialShare from '@/components/AuthorSocialShare';
import styles from './Article.module.scss';

const postType = 'post';

/**
 * Render the Post component
 *
 * @param  {object}  props           component attributes as props
 * @param  {object}  props.post      post data from WP
 * @param  {Array}   props.posts     array of posts
 * @param  {boolean} props.isArchive whether the page is an archive page or individual post
 * @return {Element}                 Post component
 */
export default function Post({ post, posts, isArchive }) {
  const { asPath } = useRouter();

  if (isArchive && posts?.length) {
    return (
      <Layout seo={{ ...post?.seo }} hasHubSpot>
        {posts.map((item) => (
          <h2>{item?.title}</h2>
        ))}
      </Layout>
    );
  }

  const topicTerm = post?.terms?.nodes.find((x) => x?.taxonomy?.node?.graphqlSingleName === 'topic');
  const taxonomyObject = {
    taxonomy: 'hdm_tax_topic',
    term: {
      name: topicTerm?.name ?? '',
      slug: topicTerm?.slug ?? '',
    },
  };

  const tagTerms = post?.terms?.nodes.filter((x) => x?.taxonomy?.node?.graphqlSingleName === 'tag' && !x.slug.includes('primary'));
  const tagTermsPrimary = post?.primaryTag?.primaryTag;

  const partner = post?.partnerArticleBranding?.partner;
  const partnerCss = {
    '--partner-accent': partner?.partnerInformation?.partnerAccentColor,
  };

  return (
    <Layout seo={{ ...post?.seo }} hasJsonLd hasHubSpot>
      {partner && (
        <div>
          <Container className={styles.partner__header}>
            <h3>{partner?.partnerInformation?.channelName}</h3>
            <div className={styles.partner__header_image}>
              <img src={partner?.partnerInformation?.partnerLogo?.mediaItemUrl} alt={partner?.title} />
            </div>
            <div className={styles.partner__header_text} style={partnerCss}>
              <div className={styles.partner__title_wrapper}>
                <a className={styles.partner__title_link} href={partner?.uri}>
                  <h1>{partner?.title}</h1>
                </a>
                <a className={styles.partner__follow} href={partner?.partnerInformation?.partnerFollowCode}>Follow</a>
              </div>
            </div>
          </Container>
        </div>
      )}
      <Container condensed>
        {tagTerms && tagTerms.length > 0 && (
          <ul className={styles.tags}>
            {tagTermsPrimary && (
              <>
                <li key={tagTermsPrimary.id} className={styles.tag}>
                  <a href={`/${tagTermsPrimary?.taxonomy?.node?.graphqlSingleName}/${tagTermsPrimary?.slug}`}>{tagTermsPrimary?.name}</a>
                </li>
              </>
          )}
            {tagTerms.filter((x) => x?.id != tagTermsPrimary?.id).map((term) => (
              <li key={term?.id} className={styles.tag}>
                <a href={`/${term?.taxonomy?.node?.graphqlSingleName}/${term?.slug}`}>{term?.name}</a>
              </li>
            ))}
          </ul>
        )}
        <BlockHeading level={1} content={post?.title} />
        <div className={styles.excerpt} dangerouslySetInnerHTML={{ __html: post?.excerpt }} />
        <div className={styles.article__meta}>
          <Moment format="MMM DD YY" className={styles.article__meta_date}>
            {post?.date}
          </Moment>
          <span className={styles.article__meta_reading}>{post?.readingTime} read</span>
        </div>
        <hr />
        <div
          className={`${styles.article__author} ${
            post?.bylineInformation?.byline2 ? styles.article__author__multiples : ''
          }`}
        >
          <div
            className={`${styles.article__author_inner} ${
              post?.bylineInformation?.byline2 ? styles.article__author_inner__multiples_author : ''
            }`}
          >
            {post?.bylineInformation?.byline ? (
              <>
                <div className={styles.author_wrapper}>
                  <a className={styles.article__author_name} href={`/byline?author=${post?.bylineInformation?.byline}`}>
                    {post?.bylineInformation?.byline}
                  </a>

                  <p>{post?.bylineInformation?.bylineTitle}
                    {(post?.bylineInformation?.bylineAffiliation && `, ${post?.bylineInformation?.bylineAffiliation}`)}</p>
                </div>
                {post?.bylineInformation?.byline2 && (
                  <div className={styles.author_wrapper}>
                    <a
                      className={styles.article__author_name}
                      href={`/byline?author=${post?.bylineInformation?.byline2}`}
                    >
                      {post?.bylineInformation?.byline2}
                    </a>

                    <p>{post?.bylineInformation?.byline2Title}
                      {(post?.bylineInformation?.byline2Affiliation && `, ${post?.bylineInformation?.byline2Affiliation}`)}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <img
                  className={styles.article__author_image}
                  src={post?.author?.node?.avatar?.url}
                  alt={post?.author?.node?.name}
                />
                <span className={styles.article__author_name}>{post?.author?.node?.name}</span>
              </>
            )}
          </div>
          {process.browser && (
            <div className={styles.article__socialShare}>
              <p>SHARE:</p>
              <SocialShare title={post?.title} url={window?.location?.href} />
            </div>
          )}
        </div>
        {isArchive && (
          <div className={styles.article__featured_image}>
            <BlockFeatureImage
              url={post?.featuredImage?.node?.sourceUrl ?? ''}
              alt={post?.featuredImage?.node?.altText}
              caption={post?.featuredImage?.node?.caption}
            />
          </div>
        )}
        <Blocks blocks={post?.blocks} />
        {/* beginning of article__author_bottom */}
        {(post?.bylineInformation?.firstFacebookSocialLink ||
          post?.bylineInformation?.firstLinkedinSocialLink ||
          post?.bylineInformation?.firstTwitterSocialLink) && (
          <div
            className={`${styles.article__author_bottom} ${
              post?.bylineInformation?.byline2 ? styles.article__author__multiples : ''
            }`}
          >
            <div
              className={`${styles.article__author_inner} ${
                post?.bylineInformation?.byline2 ? styles.article__author_inner__multiples_author : ''
              }`}
            >
              {post?.bylineInformation?.byline ? (
                <>
                  <div className={styles.author_wrapper}>
                    <span className={styles.article__author_name}>{post?.bylineInformation?.byline}</span>
                    <p>{post?.bylineInformation?.bylineTitle}</p>
                  </div>
                </>
              ) : (
                <>
                  <img
                    className={styles.article__author_image}
                    src={post?.author?.node?.avatar?.url}
                    alt={post?.author?.node?.name}
                  />
                  <span className={styles.article__author_name}>{post?.author?.node?.name}</span>
                </>
              )}
            </div>
            {(post?.bylineInformation?.firstFacebookSocialLink ||
              post?.bylineInformation?.firstLinkedinSocialLink ||
              post?.bylineInformation?.firstTwitterSocialLink) && (
              <div className={styles.article__socialShare}>
                <p>FOLLOW:</p>
                <AuthorSocialShare
                  twitter={post?.bylineInformation?.firstTwitterSocialLink}
                  facebook={post?.bylineInformation?.firstFacebookSocialLink}
                  linkedIn={post?.bylineInformation?.firstLinkedinSocialLink}
                />
              </div>
            )}
          </div>
        )}
        {(post?.bylineInformation?.secondFacebookSocialLink ||
          post?.bylineInformation?.secondLinkedinSocialLink ||
          post?.bylineInformation?.secondTwitterSocialLink) && (
          <div
            className={`${styles.article__author_bottom} ${
              post?.bylineInformation?.byline2 ? styles.article__author__multiples : ''
            }`}
          >
            <div className={styles.article__author_inner}>
              {post?.bylineInformation?.byline2 ? (
                <>
                  <div className={styles.author_wrapper}>
                    <span className={styles.article__author_name}>{post?.bylineInformation?.byline2}</span>
                    <p>{post?.bylineInformation?.byline2Title}</p>
                  </div>
                </>
              ) : (
                <span>
                  <img
                    className={styles.article__author_image}
                    src={post?.author?.node?.avatar?.url}
                    alt={post?.author?.node?.name}
                  />
                  <span className={styles.article__author_name}>{post?.author?.node?.name}</span>
                </span>
              )}
            </div>
            {(post?.bylineInformation?.secondFacebookSocialLink ||
              post?.bylineInformation?.secondLinkedinSocialLink ||
              post?.bylineInformation?.secondTwitterSocialLink) && (
              <div className={styles.article__socialShare}>
                <p>FOLLOW:</p>
                <AuthorSocialShare
                  twitter={post?.bylineInformation?.secondTwitterSocialLink}
                  facebook={post?.bylineInformation?.secondFacebookSocialLink}
                  linkedIn={post?.bylineInformation?.secondLinkedinSocialLink}
                />
              </div>
            )}
          </div>
        )}
        {/* end of article__author_bottom */}
      </Container>
      <Container>
        {topicTerm && topicTerm?.name && topicTerm?.slug && (
          <BlockArticleColumns columns={4} manualSelect={false} title="More for you" topic={taxonomyObject} />
        )}
      </Container>
    </Layout>
  );
}

/**
 * Get post static paths.
 *
 * @return {object} Array of paths and fallbacks
 */
export async function getStaticPaths() {
  return getPostTypeStaticPaths(postType);
}

/**
 * Get post static props
 *
 * @param  {object} context             context for current post
 * @param  {object} context.params      route parameters for current post
 * @param           context.preview
 * @param           context.previewData
 * @param           context.preview
 * @param           context.previewData
 * @return {object}                     post props
 */
export async function getStaticProps({ params, preview, previewData }) {
  return getPostTypeStaticProps(params, postType, preview, previewData);
}
